import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
  CLOSED_ADMIN_CAPABILITIES,
  DEFAULT_MAINTENANCE_MESSAGE,
  normalizeAccessState,
} from '../utils/campaignAccessNormalize';
import { useAuth } from './AuthContext';

const ACCESS_ERROR_MESSAGE = 'Campaign access could not be verified. Please try again.';

const closedAccessState = {
  creationEnabled: false,
  automationEnabled: false,
  canStart: false,
  canAutomate: false,
  capabilities: CLOSED_ADMIN_CAPABILITIES,
  maintenanceMessage: DEFAULT_MAINTENANCE_MESSAGE
};

const CampaignAccessContext = createContext(undefined);

export function CampaignAccessProvider({ children }) {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || null;
  const requestIdRef = useRef(0);
  const [state, setState] = useState({
    ...closedAccessState,
    loading: Boolean(userId),
    error: null,
    resolvedForUserId: userId
  });

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!userId) {
      const signedOutState = {
        ...closedAccessState,
        loading: false,
        error: null,
        resolvedForUserId: null
      };
      setState(signedOutState);
      return signedOutState;
    }

    setState({
      ...closedAccessState,
      loading: true,
      error: null,
      resolvedForUserId: userId
    });

    try {
      const getCampaignAccessState = httpsCallable(functions, 'getCampaignAccessState');
      const response = await getCampaignAccessState();
      const nextState = {
        ...normalizeAccessState(response?.data),
        loading: false,
        error: null,
        resolvedForUserId: userId
      };

      if (requestId === requestIdRef.current) setState(nextState);
      return nextState;
    } catch (_error) {
      const failedState = {
        ...closedAccessState,
        loading: false,
        error: ACCESS_ERROR_MESSAGE,
        resolvedForUserId: userId
      };

      if (requestId === requestIdRef.current) setState(failedState);
      return failedState;
    }
  }, [userId]);

  useEffect(() => {
    refresh();
    return () => {
      requestIdRef.current += 1;
    };
  }, [refresh]);

  const { resolvedForUserId, ...resolvedState } = state;
  const publicState = resolvedForUserId === userId
    ? resolvedState
    : { ...closedAccessState, loading: Boolean(userId), error: null };

  return (
    <CampaignAccessContext.Provider value={{ ...publicState, refresh }}>
      {children}
    </CampaignAccessContext.Provider>
  );
}

export function useCampaignAccess() {
  const context = useContext(CampaignAccessContext);
  if (context === undefined) {
    throw new Error('useCampaignAccess must be used within a CampaignAccessProvider');
  }
  return context;
}
